import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { api, type CreateOrderPayload } from '../lib/api';

interface FilingState {
  companyName: string;
  companyNumber: string;
  isFirstFiling: boolean;
  periodStart: string;
  periodEnd: string;
  figures: CreateOrderPayload['figures'];
}

export function AuthCode() {
  const { number } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filing = location.state as FilingState | null;

  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [directorName, setDirectorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeValid = /^[A-Z0-9]{6}$/i.test(code);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const directorValid = directorName.trim().length >= 2;
  const ready = codeValid && emailValid && directorValid && filing;

  // Filing state will be missing if the user landed here directly (e.g.
  // pasted URL or refreshed). Push them back to the Confirm page in that case.
  if (!filing) {
    return (
      <WizardLayout step="auth">
        <div className="card">
          <h1 className="text-2xl font-semibold text-slate-900">Session expired</h1>
          <p className="mt-2 text-sm text-slate-600">
            We need to re-fetch your company details. Please go back and confirm the balance
            sheet again.
          </p>
          <button onClick={() => navigate(`/confirm/${number}`)} className="btn-primary mt-6">
            Back to confirmation
          </button>
        </div>
      </WizardLayout>
    );
  }

  async function handleSubmit() {
    if (!ready || !filing) return;
    setLoading(true);
    setError(null);
    try {
      const { checkoutUrl } = await api.createOrder({
        email,
        companyNumber: filing.companyNumber,
        companyName: filing.companyName,
        directorName: directorName.trim(),
        isFirstFiling: filing.isFirstFiling,
        periodStart: filing.periodStart,
        periodEnd: filing.periodEnd,
        authCode: code,
        figures: filing.figures,
      });
      // Stripe-hosted checkout – full redirect.
      // Use `replace` rather than assigning to href so the auth-code page
      // is removed from browser history. That stops "browser back" from
      // returning the user to the auth-code form mid-payment, where they
      // could re-submit and create a duplicate order + duplicate charge
      // for the same company.
      window.location.replace(checkoutUrl);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not start checkout');
      setLoading(false);
    }
  }

  return (
    <WizardLayout step="auth">
      <div className="card">
        <h1 className="text-2xl font-semibold text-slate-900">Authentication code &amp; email</h1>
        <p className="mt-2 text-sm text-slate-600">
          Enter your 6-character Companies House authentication code for{' '}
          <span className="font-medium">{filing.companyName}</span>. Your code is held only for the
          moment of submission and never stored.
        </p>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Authentication code
            </label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={6}
              autoComplete="off"
              spellCheck={false}
              className="input mt-1 text-center text-2xl tracking-[0.4em]"
              placeholder="••••••"
            />
            <p className="mt-1 text-xs text-slate-500">6 characters, alphanumeric.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              Your email address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="input mt-1"
              placeholder="director@example.com"
            />
            <p className="mt-1 text-xs text-slate-500">
              We'll email your payment receipt and Companies House confirmation here.
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700">
              Director name (signing the accounts)
            </label>
            <input
              type="text"
              value={directorName}
              onChange={(e) => setDirectorName(e.target.value)}
              autoComplete="name"
              className="input mt-1"
              placeholder="e.g. Jane Smith"
            />
            <p className="mt-1 text-xs text-slate-500">
              This name appears on the accounts as the approving director.
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="mt-8 flex justify-between">
          <button
            onClick={() => navigate(-1)}
            className="btn-ghost"
            disabled={loading}
          >
            Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={!ready || loading}
            className="btn-primary"
          >
            {loading ? 'Redirecting to payment…' : 'Continue to payment →'}
          </button>
        </div>

        <p className="mt-6 text-[11px] text-slate-500">
          You'll be sent to Stripe's secure payment page. After payment we'll file your accounts
          and email you the Companies House confirmation.
        </p>
      </div>
    </WizardLayout>
  );
}
